/**
 * First-visit welcome popup for the community impact figures on the landing page.
 */

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import type { PublicFundImpact } from './useSavingsPlans'
import { FundImpactCard, hasFundImpact } from './FundImpactCard'

const STORAGE_KEY = 'ifl.fundImpactWelcomeSeen'

export function FundImpactWelcomeModal({
  fundImpact,
  loading,
}: {
  fundImpact: PublicFundImpact
  loading: boolean
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (loading || !hasFundImpact(fundImpact)) return
    if (localStorage.getItem(STORAGE_KEY)) return

    const timer = window.setTimeout(() => setOpen(true), 400)
    return () => window.clearTimeout(timer)
  }, [loading, fundImpact])

  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  function dismiss() {
    localStorage.setItem(STORAGE_KEY, '1')
    setOpen(false)
  }

  if (!open || !hasFundImpact(fundImpact)) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
      <button
        type="button"
        aria-label={t('common.close')}
        className="absolute inset-0 bg-stone-900/45 backdrop-blur-[2px]"
        onClick={dismiss}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('landing.fundImpactLabel')}
        className="relative w-full max-w-md motion-safe:animate-[fade-in_0.25s_ease-out]"
      >
        <FundImpactCard fundImpact={fundImpact} className="shadow-xl" />
        <button
          type="button"
          className="landing-cta-primary tap-target mt-3 w-full rounded-xl px-5 py-3.5 text-base font-bold text-white"
          onClick={dismiss}
        >
          {t('landing.fundImpactContinue')}
        </button>
      </div>
    </div>,
    document.body,
  )
}
